// Gap E pre-render route guard (RFC-0015 Phase 1, Task 1.3).
//
// AD-0001 §17 keeps login gating client-side; this guard operates WITHIN that
// decision, not against it. It withholds a protected page's content until the
// AuthProvider bootstrap (`GET /api/me`) resolves, so following a shared link
// while signed out shows NO flash of protected skeleton/error chrome before the
// existing 401 interceptor (context/auth.tsx) bounces to /login.
//
// It is PURELY a render gate over the existing `useAuth()` state:
//   - no data fetching of its own,
//   - no redirect logic of its own — the 401 interceptor owns the /login bounce.
//
// It renders inside PortalLayout's AuthProvider subtree (route elements render
// under <Outlet/>), so `useAuth()` resolves. AuthProvider's mount is unchanged.

import type { ReactNode } from 'react';
import { useAuth } from '@/context/auth';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  // Still bootstrapping — a neutral, content-free placeholder (never the
  // protected page's own skeleton chrome).
  if (isLoading) {
    return <div role="status" aria-busy="true" aria-label="Loading" />;
  }

  // Resolved unauthenticated — render nothing while the existing 401-driven
  // redirect fires. Do NOT duplicate the redirect here.
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
